import React, { useState, useEffect } from 'react';
import { Plus, Tag, Edit, Trash2, Check, X } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';

const AdminCoupons = () => {
  const { fetchWithAuth } = useAdminAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '', description: '', discount_type: 'percentage', discount_value: 10,
    min_purchase_amount: 0, max_discount_amount: null, usage_limit: null,
    valid_from: '', valid_until: '', is_active: true
  });

  useEffect(() => { loadCoupons(); }, []);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth('/api/admin/coupons');
      if (res.ok) { const data = await res.json(); setCoupons(data.coupons); }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...formData, valid_from: new Date(formData.valid_from).toISOString(), valid_until: new Date(formData.valid_until).toISOString() };
      const res = editingCoupon
        ? await fetchWithAuth(`/api/admin/coupons/${editingCoupon.id}`, { method: 'PUT', body })
        : await fetchWithAuth('/api/admin/coupons', { method: 'POST', body });
      if (res.ok) {
        toast.success(editingCoupon ? 'Cupón actualizado' : 'Cupón creado');
        setShowModal(false);
        setEditingCoupon(null);
        resetForm();
        loadCoupons();
      } else { const err = await res.json(); toast.error(err.detail || 'Error'); }
    } catch (error) { toast.error('Error'); }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm('¿Eliminar este cupón?')) return;
    try {
      const res = await fetchWithAuth(`/api/admin/coupons/${id}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Cupón eliminado'); loadCoupons(); }
    } catch (error) { toast.error('Error'); }
  };

  const openEditModal = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code, description: coupon.description, discount_type: coupon.discount_type,
      discount_value: coupon.discount_value, min_purchase_amount: coupon.min_purchase_amount || 0,
      max_discount_amount: coupon.max_discount_amount, usage_limit: coupon.usage_limit,
      valid_from: coupon.valid_from?.slice(0, 16), valid_until: coupon.valid_until?.slice(0, 16), is_active: coupon.is_active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ code: '', description: '', discount_type: 'percentage', discount_value: 10, min_purchase_amount: 0, max_discount_amount: null, usage_limit: null, valid_from: '', valid_until: '', is_active: true });
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(v);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX') : '-';

  return (
    <div className="space-y-6" data-testid="admin-coupons">
      <div className="flex items-center justify-between">
        <div><h2 className="text-2xl font-semibold text-white">Cupones</h2><p className="text-gray-400 text-sm">{coupons.length} cupones</p></div>
        <button onClick={() => { resetForm(); setEditingCoupon(null); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg">
          <Plus size={18} />Nuevo Cupón
        </button>
      </div>

      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Código</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Descuento</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Uso</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Validez</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Estado</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-300">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr><td colSpan="6" className="px-4 py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div></td></tr>
              ) : coupons.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-12 text-center text-gray-400"><Tag className="mx-auto mb-3" size={40} /><p>No hay cupones</p></td></tr>
              ) : coupons.map(coupon => (
                <tr key={coupon.id} className="hover:bg-gray-700/30">
                  <td className="px-4 py-3">
                    <p className="text-sm text-white font-mono font-medium">{coupon.code}</p>
                    <p className="text-xs text-gray-400">{coupon.description}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-amber-500 font-medium">
                      {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : formatCurrency(coupon.discount_value)}
                    </span>
                    {coupon.min_purchase_amount > 0 && <p className="text-xs text-gray-400">Mín: {formatCurrency(coupon.min_purchase_amount)}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-white">{coupon.usage_count || 0}</span>
                    {coupon.usage_limit && <span className="text-gray-400"> / {coupon.usage_limit}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs text-gray-300">{formatDate(coupon.valid_from)}</p>
                    <p className="text-xs text-gray-400">hasta {formatDate(coupon.valid_until)}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${coupon.is_active ? 'bg-green-500/10 text-green-400' : 'bg-gray-600 text-gray-400'}`}>
                      {coupon.is_active ? <Check size={12} /> : <X size={12} />}
                      {coupon.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEditModal(coupon)} className="p-2 text-gray-400 hover:text-amber-500"><Edit size={16} /></button>
                    <button onClick={() => deleteCoupon(coupon.id)} className="p-2 text-gray-400 hover:text-red-500"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowModal(false)}>
          <div className="bg-gray-800 rounded-xl max-w-lg w-full my-8 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <form onSubmit={handleSubmit} className="p-6">
              <h3 className="text-xl font-semibold text-white mb-6">{editingCoupon ? 'Editar Cupón' : 'Nuevo Cupón'}</h3>
              <div className="space-y-4">
                <div><label className="block text-sm text-gray-300 mb-2">Código</label><input type="text" value={formData.code} onChange={e => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white uppercase" required disabled={!!editingCoupon} /></div>
                <div><label className="block text-sm text-gray-300 mb-2">Descripción</label><input type="text" value={formData.description} onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white" required /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm text-gray-300 mb-2">Tipo</label><select value={formData.discount_type} onChange={e => setFormData(prev => ({ ...prev, discount_type: e.target.value }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white"><option value="percentage">Porcentaje</option><option value="fixed_amount">Monto fijo</option></select></div>
                  <div><label className="block text-sm text-gray-300 mb-2">Valor</label><input type="number" value={formData.discount_value} onChange={e => setFormData(prev => ({ ...prev, discount_value: Number(e.target.value) }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white" required /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm text-gray-300 mb-2">Compra mínima</label><input type="number" value={formData.min_purchase_amount} onChange={e => setFormData(prev => ({ ...prev, min_purchase_amount: Number(e.target.value) }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white" /></div>
                  <div><label className="block text-sm text-gray-300 mb-2">Límite de uso</label><input type="number" value={formData.usage_limit || ''} onChange={e => setFormData(prev => ({ ...prev, usage_limit: e.target.value ? Number(e.target.value) : null }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white" placeholder="Sin límite" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm text-gray-300 mb-2">Válido desde</label><input type="datetime-local" value={formData.valid_from} onChange={e => setFormData(prev => ({ ...prev, valid_from: e.target.value }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white" required /></div>
                  <div><label className="block text-sm text-gray-300 mb-2">Válido hasta</label><input type="datetime-local" value={formData.valid_until} onChange={e => setFormData(prev => ({ ...prev, valid_until: e.target.value }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white" required /></div>
                </div>
                <label className="flex items-center gap-3"><input type="checkbox" checked={formData.is_active} onChange={e => setFormData(prev => ({ ...prev, is_active: e.target.checked }))} className="w-4 h-4 accent-amber-500" /><span className="text-sm text-gray-300">Cupón activo</span></label>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-700 text-white rounded-lg">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-amber-500 text-black font-medium rounded-lg">{editingCoupon ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoupons;
